import React, { useEffect, useState, useMemo } from 'react';
import { api } from '../../api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../../components/ui/dialog';
import {
  PenSquare, Sparkles, Save, Trash2, Eye, Rocket, RotateCcw, Loader2, Plus, Copy, ArrowUpRight, ListTree, FileText, Share2, Clock, Users, Download, Upload,
} from 'lucide-react';

// ----------------------------------------------------------------------
// AdminBlog — write, AI-assist, publish, and share blog posts.
// ----------------------------------------------------------------------
// Layout: two-pane. Left = post list + "New post". Right = editor with
//   tabs for Content, SEO, and Preview. AI buttons live inline next to
//   the fields they populate. Publish/Unpublish + Copy public URL sit
//   in the sticky editor header.
// ----------------------------------------------------------------------

const SITE_ORIGIN = 'https://k9os.app';
const EMPTY = {
  id: '', title: '', slug: '', excerpt: '', cover_image: '',
  content_markdown: '', seo_title: '', seo_description: '',
  keywords: [], tags: [], status: 'DRAFT', author: '',
};

const slugify = (t) => (t || '').toLowerCase().trim()
  .replace(/[^a-z0-9\s-]/g, '')
  .replace(/[\s_-]+/g, '-')
  .replace(/^-+|-+$/g, '');

// Rolling default publish slot: next Sunday at 8:00 AM local time.
// Users can override any date/time; this just seeds the picker with a
// sensible weekly cadence that matches the K9os editorial calendar.
const nextSunday8am = () => {
  const d = new Date();
  const day = d.getDay(); // 0 = Sun
  const daysUntilSun = day === 0 ? 7 : (7 - day);
  d.setDate(d.getDate() + daysUntilSun);
  d.setHours(8, 0, 0, 0);
  return d;
};

// Convert Date -> value suitable for <input type="datetime-local"> which
// requires 'YYYY-MM-DDTHH:MM' in LOCAL time (no timezone suffix).
const toLocalInputValue = (d) => {
  if (!d) return '';
  const dt = typeof d === 'string' ? new Date(d) : d;
  if (Number.isNaN(dt.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
};

// Human-friendly formatter used in the "scheduled for …" hint text.
const formatScheduled = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  } catch { return iso; }
};

const AiButton = ({ onClick, loading, label, testid }) => (
  <Button
    type='button'
    variant='outline'
    size='sm'
    onClick={onClick}
    disabled={loading}
    data-testid={testid}
    className='gap-1.5 border-[#C8102E]/40 text-[#C8102E] hover:bg-[#C8102E]/5 hover:text-[#C8102E] dark:text-[#F87171] dark:border-[#F87171]/40'
  >
    {loading ? <Loader2 className='w-3.5 h-3.5 animate-spin' /> : <Sparkles className='w-3.5 h-3.5' />}
    {label}
  </Button>
);

const KeywordChips = ({ items, onChange }) => {
  const [draft, setDraft] = useState('');
  const add = (v) => {
    const clean = (v || '').trim().toLowerCase();
    if (!clean) return;
    if (items.includes(clean)) return;
    onChange([...items, clean]);
    setDraft('');
  };
  return (
    <div className='flex flex-wrap gap-2 items-center'>
      {items.map((k, i) => (
        <Badge
          key={k + i}
          variant='outline'
          className='pl-2 pr-1 py-1 gap-1 border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 text-xs'
        >
          {k}
          <button
            type='button'
            onClick={() => onChange(items.filter((_, idx) => idx !== i))}
            className='w-4 h-4 rounded-full hover:bg-slate-200 dark:hover:bg-slate-700 flex items-center justify-center text-slate-500'
            aria-label={`Remove ${k}`}
          >
            ×
          </button>
        </Badge>
      ))}
      <input
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            add(draft);
          }
        }}
        onBlur={() => draft && add(draft)}
        placeholder='Add keyword + Enter'
        className='min-w-[160px] flex-1 bg-transparent text-xs px-2 py-1 border border-dashed border-slate-300 dark:border-slate-700 rounded-md focus:outline-none focus:border-[#1B3A87]'
        data-testid='blog-keyword-input'
      />
    </div>
  );
};

// Very small markdown → HTML preview (headings, bold, italics, code,
// links, lists, paragraphs). We keep this tiny + safe (no eval of HTML)
// so we don't add a runtime dep just for preview.
const renderMarkdownPreview = (md) => {
  if (!md) return '<p class="text-slate-400 italic">Nothing yet — start writing or generate a draft.</p>';
  const esc = (s) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const lines = md.split(/\r?\n/);
  const out = [];
  let inList = false;
  for (let raw of lines) {
    const line = raw.trimEnd();
    if (/^\s*[-*]\s+/.test(line)) {
      if (!inList) { out.push('<ul class="list-disc pl-5 my-3 space-y-1">'); inList = true; }
      out.push(`<li>${esc(line.replace(/^\s*[-*]\s+/, ''))}</li>`);
      continue;
    }
    if (inList) { out.push('</ul>'); inList = false; }
    if (/^###\s+/.test(line)) { out.push(`<h3 class="mt-4 mb-2 font-display text-lg font-bold">${esc(line.replace(/^###\s+/, ''))}</h3>`); continue; }
    if (/^##\s+/.test(line))  { out.push(`<h2 class="mt-6 mb-2 font-display text-xl font-bold">${esc(line.replace(/^##\s+/, ''))}</h2>`); continue; }
    if (/^#\s+/.test(line))   { out.push(`<h1 class="mt-6 mb-3 font-display text-2xl font-bold">${esc(line.replace(/^#\s+/, ''))}</h1>`); continue; }
    if (!line.trim()) { out.push(''); continue; }
    let html = esc(line);
    html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');
    html = html.replace(/`([^`]+)`/g, '<code class="px-1 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-[13px]">$1</code>');
    html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-[#1B3A87] dark:text-[#93C5FD] underline" target="_blank" rel="noopener noreferrer">$1</a>');
    out.push(`<p class="my-3 leading-relaxed">${html}</p>`);
  }
  if (inList) out.push('</ul>');
  return out.join('\n');
};

export default function AdminBlog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState('content'); // content | seo | preview
  const [busy, setBusy] = useState({ seo: false, outline: false, draft: false, cover: false });
  const [outline, setOutline] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/blog');
      setPosts(data.posts || []);
    } catch (e) {
      toast.error('Failed to load posts');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openPost = (p) => {
    setSelected({ ...EMPTY, ...p });
    setOutline([]);
    setTab('content');
  };

  const startNew = () => {
    setSelected({ ...EMPTY });
    setOutline([]);
    setTab('content');
  };

  const setField = (k, v) => setSelected((cur) => ({ ...cur, [k]: v }));

  const savePost = async ({ silent = false } = {}) => {
    if (!selected) return;
    if (!selected.title.trim()) {
      toast.error('Title is required');
      return null;
    }
    setSaving(true);
    try {
      const payload = {
        title: selected.title.trim(),
        slug: selected.slug || slugify(selected.title),
        excerpt: selected.excerpt,
        cover_image: selected.cover_image || null,
        content_markdown: selected.content_markdown,
        seo_title: selected.seo_title,
        seo_description: selected.seo_description,
        keywords: selected.keywords,
        tags: selected.tags,
        status: selected.status,
        author: selected.author || undefined,
      };
      const url = selected.id ? `/admin/blog/${selected.id}` : '/admin/blog';
      const method = selected.id ? 'patch' : 'post';
      const { data } = await api[method](url, payload);
      setSelected({ ...EMPTY, ...data });
      await load();
      if (!silent) toast.success(selected.id ? 'Post saved' : 'Post created');
      return data;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
      return null;
    } finally {
      setSaving(false);
    }
  };

  const publishToggle = async () => {
    if (!selected?.id) {
      toast.error('Save the post first');
      return;
    }
    const url = selected.status === 'PUBLISHED'
      ? `/admin/blog/${selected.id}/unpublish`
      : `/admin/blog/${selected.id}/publish`;
    try {
      const { data } = await api.post(url);
      setSelected({ ...EMPTY, ...data });
      await load();
      toast.success(data.status === 'PUBLISHED' ? 'Published' : 'Moved to draft');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const doDelete = async (id) => {
    try {
      await api.delete(`/admin/blog/${id}`);
      setConfirmDelete(null);
      if (selected?.id === id) setSelected(null);
      await load();
      toast.success('Post deleted');
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  // ---- AI helpers ------------------------------------------------------
  const aiGenerateSeo = async () => {
    if (!selected?.title.trim()) {
      toast.error('Enter a title first — that\'s the AI topic');
      return;
    }
    setBusy((b) => ({ ...b, seo: true }));
    try {
      const { data } = await api.post('/admin/blog/ai/seo', { topic: selected.title });
      setSelected((cur) => ({
        ...cur,
        seo_title: data.seo_title || cur.seo_title,
        seo_description: data.seo_description || cur.seo_description,
        keywords: Array.from(new Set([...(cur.keywords || []), ...(data.keywords || [])])).slice(0, 20),
      }));
      toast.success('AI SEO metadata generated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'AI SEO failed');
    } finally {
      setBusy((b) => ({ ...b, seo: false }));
    }
  };

  const aiGenerateOutline = async () => {
    if (!selected?.title.trim()) {
      toast.error('Enter a title first');
      return;
    }
    setBusy((b) => ({ ...b, outline: true }));
    try {
      const { data } = await api.post('/admin/blog/ai/outline', {
        topic: selected.title,
        seo_title: selected.seo_title,
        keywords: selected.keywords,
      });
      setOutline(data.outline || []);
      toast.success('Outline generated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'AI outline failed');
    } finally {
      setBusy((b) => ({ ...b, outline: false }));
    }
  };

  const aiGenerateDraft = async () => {
    if (!selected?.title.trim()) {
      toast.error('Enter a title first');
      return;
    }
    setBusy((b) => ({ ...b, draft: true }));
    try {
      const { data } = await api.post('/admin/blog/ai/draft', {
        title: selected.title,
        seo_title: selected.seo_title,
        seo_description: selected.seo_description,
        keywords: selected.keywords,
        outline,
      });
      setField('content_markdown', data.markdown || selected.content_markdown);
      toast.success('AI draft written');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'AI draft failed');
    } finally {
      setBusy((b) => ({ ...b, draft: false }));
    }
  };

  // Generates a branded 16:9 blog cover with Gemini Nano Banana and drops
  // the base64 data URI straight into cover_image so social shares (X,
  // Facebook, LinkedIn, iMessage) stop showing the generic OG image.
  const aiGenerateCover = async () => {
    if (!selected?.title.trim()) {
      toast.error('Enter a title first — that\u2019s the AI cover prompt');
      return;
    }
    setBusy((b) => ({ ...b, cover: true }));
    try {
      const { data } = await api.post('/admin/blog/ai/cover', {
        title: selected.title,
        keywords: selected.keywords || [],
      });
      if (data.cover_image) {
        setField('cover_image', data.cover_image);
        toast.success('AI cover generated');
      } else {
        toast.error('Cover generation returned nothing');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'AI cover failed');
    } finally {
      setBusy((b) => ({ ...b, cover: false }));
    }
  };

  // Upload a cover image from the admin's device (phone gallery, phone
  // camera, or desktop file picker). We convert to a base64 data URI and
  // store it in `cover_image` — the exact same format `aiGenerateCover`
  // uses — so the rest of the publish/preview/social-share pipeline needs
  // zero changes. Guardrails: 10 MB max + explicit image mime-type check
  // so the editor never accidentally uploads a non-image (PDF, video, …).
  //
  // 16:9 CENTER-CROP: Blog cover images render inside a 16:9 aspect box
  // (Open Graph share image standard, K9os social cards, blog list
  // thumbnails). Uploads from a phone gallery are usually 4:3 or 3:4 which
  // would either letterbox or get awkwardly stretched. To keep every cover
  // consistent, we auto-crop the uploaded image to 16:9 on a canvas
  // before storing it. The crop is a CENTER crop (removes symmetric bars
  // from the sides or top/bottom of the source) which preserves the
  // subject of most phone photos. Output is JPEG at 92% quality — good
  // trade-off between file size and visual fidelity for hero photos.
  const MAX_UPLOAD_BYTES = 10 * 1024 * 1024; // 10 MB
  const cropTo16by9 = (dataUri) => new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      // Target width caps at 1600 (matches OG spec + shrinks big phone
      // photos so we don't push 8 MB base64 into Mongo).
      const targetW = Math.min(1600, img.naturalWidth);
      const targetH = Math.round((targetW * 9) / 16);
      // Source crop rectangle inside the original image (center-crop).
      const srcAspect = img.naturalWidth / img.naturalHeight;
      const dstAspect = 16 / 9;
      let sx; let sy; let sw; let sh;
      if (srcAspect > dstAspect) {
        // Source is WIDER than 16:9 → crop the left+right bars.
        sh = img.naturalHeight;
        sw = Math.round(sh * dstAspect);
        sx = Math.round((img.naturalWidth - sw) / 2);
        sy = 0;
      } else {
        // Source is TALLER than 16:9 (or already exactly 16:9) → crop top+bottom.
        sw = img.naturalWidth;
        sh = Math.round(sw / dstAspect);
        sx = 0;
        sy = Math.round((img.naturalHeight - sh) / 2);
      }
      const canvas = document.createElement('canvas');
      canvas.width = targetW;
      canvas.height = targetH;
      const ctx = canvas.getContext('2d');
      if (!ctx) { reject(new Error('canvas-unavailable')); return; }
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, sx, sy, sw, sh, 0, 0, targetW, targetH);
      try {
        resolve(canvas.toDataURL('image/jpeg', 0.92));
      } catch (err) {
        reject(err);
      }
    };
    img.onerror = () => reject(new Error('image-decode-failed'));
    img.src = dataUri;
  });
  const handleCoverUpload = (event) => {
    const file = event?.target?.files?.[0];
    // Always reset the input so re-selecting the same file still fires
    // onChange the next time the user opens the picker.
    if (event?.target) event.target.value = '';
    if (!file) return;
    if (!file.type || !file.type.startsWith('image/')) {
      toast.error('Please choose an image file (JPG, PNG, WebP, HEIC, …)');
      return;
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      toast.error(`Image is too large — max ${(MAX_UPLOAD_BYTES / (1024 * 1024)).toFixed(0)} MB`);
      return;
    }
    setBusy((b) => ({ ...b, cover: true }));
    const reader = new FileReader();
    reader.onload = async () => {
      if (typeof reader.result !== 'string') {
        toast.error('Could not read the image — please try a different file');
        setBusy((b) => ({ ...b, cover: false }));
        return;
      }
      try {
        const cropped = await cropTo16by9(reader.result);
        setField('cover_image', cropped);
        toast.success('Cover uploaded — auto-cropped to 16:9 for social shares');
      } catch (err) {
        // Fallback to the raw upload if canvas cropping fails (rare, but
        // e.g. iOS Safari + HEIC without conversion). At least the user
        // still gets their image saved — social shares will letterbox.
        setField('cover_image', reader.result);
        toast.warning('Cover uploaded — auto-crop failed, using original aspect');
      } finally {
        setBusy((b) => ({ ...b, cover: false }));
      }
    };
    reader.onerror = () => {
      toast.error('Could not read the image — please try again');
      setBusy((b) => ({ ...b, cover: false }));
    };
    reader.readAsDataURL(file);
  };

  // ---- Subscribers ------------------------------------------------------
  // Kept on the same admin page as the blog editor so the workflow stays
  // in one screen (write → publish → grow list).
  const [view, setView] = useState('posts'); // 'posts' | 'subscribers'
  const [subs, setSubs] = useState([]);
  const [subsLoading, setSubsLoading] = useState(false);

  const loadSubs = async () => {
    setSubsLoading(true);
    try {
      const { data } = await api.get('/admin/blog-subscribers');
      setSubs(data.subscribers || []);
    } catch {
      toast.error('Failed to load subscribers');
    } finally {
      setSubsLoading(false);
    }
  };
  useEffect(() => {
    if (view === 'subscribers') loadSubs();
  }, [view]);

  const removeSub = async (id) => {
    try {
      await api.delete(`/admin/blog-subscribers/${id}`);
      setSubs((s) => s.filter((x) => x.id !== id));
      toast.success('Subscriber removed');
    } catch {
      toast.error('Remove failed');
    }
  };

  const exportCsv = async () => {
    try {
      // The backend serves a signed CSV; open in a new tab so the browser
      // uses the Authorization header from a lightweight refetch. To keep
      // things simple we hit the endpoint via axios, then blob-download.
      const resp = await api.get('/admin/blog-subscribers.csv', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url; a.download = 'k9os-blog-subscribers.csv';
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('CSV export failed');
    }
  };

  // ---- Sharing ---------------------------------------------------------
  const publicUrl = useMemo(() => selected?.slug ? `${SITE_ORIGIN}/blog/${selected.slug}` : '', [selected?.slug]);
  const shareLinks = useMemo(() => {
    if (!publicUrl) return null;
    const u = encodeURIComponent(publicUrl);
    const t = encodeURIComponent(selected?.seo_title || selected?.title || '');
    return {
      copy: publicUrl,
      x: `https://twitter.com/intent/tweet?url=${u}&text=${t}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${u}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${u}`,
    };
  }, [publicUrl, selected?.seo_title, selected?.title]);

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      toast.success('Public URL copied');
    } catch {
      toast.error('Copy failed');
    }
  };

  // ---- Render ----------------------------------------------------------
  return (
    <div className='p-4 sm:p-6 max-w-[1440px] mx-auto' data-testid='admin-blog'>
      <div className='mb-6 flex items-center justify-between gap-3 flex-wrap'>
        <div>
          <div className='text-xs uppercase tracking-[0.18em] font-bold text-[#C8102E]'>Content Engine</div>
          <h1 className='font-display text-2xl sm:text-3xl font-bold text-[#0B1220] dark:text-slate-50 mt-1 flex items-center gap-2'>
            <PenSquare className='w-6 h-6 text-[#1B3A87] dark:text-[#93C5FD]' /> Blog
          </h1>
          <p className='text-sm text-slate-500 dark:text-slate-400 mt-1'>Write, AI-assist, publish, and share dog-trainer SEO articles.</p>
        </div>
        <div className='flex items-center gap-2 flex-wrap'>
          {/* View toggle — Posts / Subscribers.
              Kept in the header so a returning admin can jump straight to the
              subscribers list without a page reload. */}
          <div className='inline-flex rounded-lg border border-slate-200 dark:border-slate-800 p-0.5 bg-slate-50 dark:bg-slate-900'>
            <button
              type='button'
              onClick={() => setView('posts')}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold inline-flex items-center gap-1.5 ${view === 'posts' ? 'bg-[#1B3A87] text-white' : 'text-slate-600 dark:text-slate-300 hover:bg-white dark:hover:bg-slate-800'}`}
              data-testid='blog-view-posts'
            >
              <FileText className='w-3.5 h-3.5' /> Posts
            </button>
            <button
              type='button'
              onClick={() => setView('subscribers')}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold inline-flex items-center gap-1.5 ${view === 'subscribers' ? 'bg-[#1B3A87] text-white' : 'text-slate-600 dark:text-slate-300 hover:bg-white dark:hover:bg-slate-800'}`}
              data-testid='blog-view-subscribers'
            >
              <Users className='w-3.5 h-3.5' /> Subscribers
            </button>
          </div>
          {view === 'posts' ? (
            <Button onClick={startNew} className='bg-[#1B3A87] hover:bg-[#16306F] text-white' data-testid='blog-new-btn'>
              <Plus className='w-4 h-4 mr-1.5' /> New post
            </Button>
          ) : (
            <Button onClick={exportCsv} variant='outline' className='gap-1.5' data-testid='blog-subs-export'>
              <Download className='w-4 h-4' /> Export CSV
            </Button>
          )}
        </div>
      </div>

      {view === 'subscribers' ? (
        <Card className='p-4'>
          <div className='flex items-center gap-3 mb-3'>
            <div className='text-sm font-semibold text-[#0B1220] dark:text-slate-50 flex items-center gap-2'>
              <Users className='w-4 h-4 text-[#1B3A87] dark:text-[#93C5FD]' /> Blog subscribers
            </div>
            <div className='text-xs text-slate-500 dark:text-slate-400'>{subs.length} total</div>
          </div>
          {subsLoading ? (
            <div className='p-6 text-sm text-slate-500 flex items-center gap-2'><Loader2 className='w-4 h-4 animate-spin' /> Loading…</div>
          ) : subs.length === 0 ? (
            <div className='p-8 text-center text-slate-500 dark:text-slate-400'>
              <Users className='w-10 h-10 mx-auto mb-3 opacity-40' />
              <p className='text-sm'>No subscribers yet. The public opt-in form on <span className='font-mono'>/blog</span> feeds this list.</p>
            </div>
          ) : (
            <div className='overflow-x-auto'>
              <table className='w-full text-sm' data-testid='blog-subs-table'>
                <thead>
                  <tr className='text-[11px] uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-800'>
                    <th className='text-left py-2 pr-3 font-semibold'>Email</th>
                    <th className='text-left py-2 pr-3 font-semibold'>Source</th>
                    <th className='text-left py-2 pr-3 font-semibold'>Subscribed</th>
                    <th className='text-right py-2 pr-1 font-semibold'>&nbsp;</th>
                  </tr>
                </thead>
                <tbody className='divide-y divide-slate-100 dark:divide-slate-800'>
                  {subs.map((s) => (
                    <tr key={s.id} data-testid={`blog-sub-row-${s.id}`}>
                      <td className='py-2 pr-3 text-slate-800 dark:text-slate-100 font-medium truncate max-w-[280px]'>{s.email}</td>
                      <td className='py-2 pr-3 text-slate-500 dark:text-slate-400 text-xs'>{s.source || 'blog'}</td>
                      <td className='py-2 pr-3 text-slate-500 dark:text-slate-400 text-xs'>{formatScheduled(s.subscribed_at)}</td>
                      <td className='py-2 pr-1 text-right'>
                        <button
                          onClick={() => removeSub(s.id)}
                          className='text-xs font-semibold text-red-600 hover:text-red-700 dark:text-red-400 hover:underline'
                          data-testid={`blog-sub-remove-${s.id}`}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      ) : (
      <div className='grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-5'>
        {/* Post list */}
        <Card className='p-3 h-fit'>
          <div className='text-xs uppercase tracking-[0.14em] font-bold text-slate-500 dark:text-slate-400 px-2 py-1'>All posts</div>
          {loading ? (
            <div className='p-4 text-sm text-slate-500 flex items-center gap-2'><Loader2 className='w-4 h-4 animate-spin' /> Loading…</div>
          ) : posts.length === 0 ? (
            <div className='p-6 text-sm text-slate-500 text-center'>
              <FileText className='w-8 h-8 mx-auto mb-2 opacity-40' />
              No posts yet. Click &ldquo;New post&rdquo; to start.
            </div>
          ) : (
            <ul className='divide-y divide-slate-100 dark:divide-slate-800'>
              {posts.map((p) => (
                <li key={p.id}>
                  <button
                    type='button'
                    onClick={() => openPost(p)}
                    className={`w-full text-left px-3 py-3 rounded-md hover:bg-slate-50 dark:hover:bg-slate-800/60 transition-colors ${selected?.id === p.id ? 'bg-slate-50 dark:bg-slate-800/70' : ''}`}
                    data-testid={`blog-list-item-${p.id}`}
                  >
                    <div className='flex items-start gap-2'>
                      <div className='flex-1 min-w-0'>
                        <div className='text-sm font-semibold text-[#0B1220] dark:text-slate-50 truncate'>{p.title || 'Untitled'}</div>
                        <div className='text-[11px] text-slate-500 dark:text-slate-400 mt-0.5 truncate'>/{p.slug}</div>
                      </div>
                      <Badge
                        variant={p.status === 'PUBLISHED' ? 'default' : 'outline'}
                        className={p.status === 'PUBLISHED'
                          ? 'bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-200 dark:border-emerald-800'
                          : ''}
                      >
                        {p.status}
                      </Badge>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Card>

        {/* Editor */}
        {!selected ? (
          <Card className='p-10 text-center text-slate-500 dark:text-slate-400'>
            <PenSquare className='w-10 h-10 mx-auto mb-3 opacity-40' />
            <p className='text-sm'>Pick a post to edit, or click <span className='font-semibold text-[#1B3A87] dark:text-[#93C5FD]'>New post</span> to write one.</p>
          </Card>
        ) : (
          <Card className='p-0 overflow-hidden'>
            {/* Sticky editor header */}
            <div className='sticky top-0 z-10 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 px-5 py-3 flex items-center gap-2 flex-wrap'>
              <div className='flex items-center gap-1 mr-auto text-sm'>
                {[
                  { key: 'content', label: 'Content', icon: FileText },
                  { key: 'seo', label: 'SEO', icon: Sparkles },
                  { key: 'preview', label: 'Preview', icon: Eye },
                ].map((t) => (
                  <button
                    key={t.key}
                    type='button'
                    onClick={() => setTab(t.key)}
                    data-testid={`blog-tab-${t.key}`}
                    className={`px-3 py-1.5 rounded-md text-xs font-semibold inline-flex items-center gap-1.5 ${
                      tab === t.key
                        ? 'bg-[#1B3A87] text-white'
                        : 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800'
                    }`}
                  >
                    <t.icon className='w-3.5 h-3.5' /> {t.label}
                  </button>
                ))}
              </div>
              <Button size='sm' variant='outline' onClick={() => savePost()} disabled={saving} data-testid='blog-save-btn'>
                {saving ? <Loader2 className='w-4 h-4 mr-1.5 animate-spin' /> : <Save className='w-4 h-4 mr-1.5' />} Save
              </Button>
              <Button
                size='sm'
                onClick={publishToggle}
                disabled={!selected.id}
                className={selected.status === 'PUBLISHED' ? 'bg-slate-600 hover:bg-slate-700 text-white' : 'bg-[#C8102E] hover:bg-[#A50D26] text-white'}
                data-testid='blog-publish-btn'
              >
                {selected.status === 'PUBLISHED'
                  ? <><RotateCcw className='w-4 h-4 mr-1.5' /> Unpublish</>
                  : <><Rocket className='w-4 h-4 mr-1.5' /> Publish</>}
              </Button>
              {selected.id && (
                <Button
                  size='sm'
                  variant='outline'
                  onClick={() => setConfirmDelete(selected.id)}
                  className='text-red-600 border-red-200 hover:bg-red-50 dark:text-red-400 dark:border-red-900'
                  data-testid='blog-delete-btn'
                >
                  <Trash2 className='w-4 h-4' />
                </Button>
              )}
            </div>

            {/* Tabs body */}
            <div className='p-5 space-y-5'>
              {tab === 'content' && (
                <>
                  <div>
                    <Label>Title</Label>
                    <Input
                      value={selected.title}
                      onChange={(e) => setField('title', e.target.value)}
                      onBlur={() => !selected.slug && setField('slug', slugify(selected.title))}
                      placeholder='e.g. How dog trainers can replace Square with an all-in-one system'
                      data-testid='blog-title-input'
                    />
                  </div>
                  <div className='grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2 sm:items-end'>
                    <div>
                      <Label>Slug</Label>
                      <Input
                        value={selected.slug}
                        onChange={(e) => setField('slug', slugify(e.target.value))}
                        placeholder='auto-generated from title'
                        data-testid='blog-slug-input'
                      />
                    </div>
                    {publicUrl && (
                      <Button variant='outline' size='sm' onClick={copyUrl} className='gap-1.5' data-testid='blog-copy-url'>
                        <Copy className='w-3.5 h-3.5' /> Copy public URL
                      </Button>
                    )}
                  </div>
                  <div>
                    <Label>Excerpt</Label>
                    <Textarea
                      value={selected.excerpt}
                      onChange={(e) => setField('excerpt', e.target.value)}
                      placeholder='2–3 sentence summary shown on the blog index and previews.'
                      rows={2}
                      data-testid='blog-excerpt-input'
                    />
                  </div>
                  <div className='rounded-xl border border-dashed border-slate-300 dark:border-slate-700 p-4'>
                    <div className='flex items-center justify-between gap-2 flex-wrap'>
                      <div className='flex items-center gap-2 text-sm font-semibold text-[#0B1220] dark:text-slate-50'>
                        <Sparkles className='w-4 h-4 text-[#C8102E]' /> Cover image (used for social shares)
                      </div>
                      <div className='flex items-center gap-2 flex-wrap'>
                        {selected.cover_image && (
                          <Button
                            type='button'
                            variant='outline'
                            size='sm'
                            onClick={() => setField('cover_image', '')}
                            data-testid='blog-cover-clear'
                          >
                            Clear
                          </Button>
                        )}
                        {/* Upload from device — accept only images. On mobile
                            the browser's picker lets the user choose between
                            "Take a photo" (rear camera) and "Photo Library"
                            because we DON'T set `capture=` explicitly — so
                            phone gallery + phone camera + desktop file picker
                            all just work. */}
                        <label
                          className='inline-flex items-center gap-2 h-9 px-3 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-[#0B1220] dark:text-slate-100 text-sm font-semibold cursor-pointer hover:border-[#1B3A87] hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors'
                          data-testid='blog-cover-upload-label'
                        >
                          {busy.cover ? (
                            <>
                              <Loader2 className='w-4 h-4 animate-spin' /> Uploading…
                            </>
                          ) : (
                            <>
                              <Upload className='w-4 h-4' /> Upload from device
                            </>
                          )}
                          <input
                            type='file'
                            accept='image/*'
                            onChange={handleCoverUpload}
                            className='sr-only'
                            data-testid='blog-cover-upload-input'
                            disabled={busy.cover}
                          />
                        </label>
                        <AiButton
                          onClick={aiGenerateCover}
                          loading={busy.cover}
                          label='AI generate cover'
                          testid='blog-ai-cover'
                        />
                      </div>
                    </div>

                    {/* Live preview — if we have an image, show a 16:9 preview;
                        otherwise show a helpful empty state. */}
                    {selected.cover_image ? (
                      <div className='mt-3 rounded-lg overflow-hidden border border-slate-200 dark:border-slate-800 aspect-[16/9] bg-slate-100 dark:bg-slate-800'>
                        <img
                          src={selected.cover_image}
                          alt='Cover preview'
                          className='w-full h-full object-cover'
                          data-testid='blog-cover-preview'
                        />
                      </div>
                    ) : (
                      <p className='mt-2 text-xs text-slate-500'>
                        No cover yet. Click <span className='font-semibold text-[#1B3A87] dark:text-[#93C5FD]'>Upload from device</span> to use your own photo,
                        <span className='font-semibold text-[#C8102E]'> AI generate cover</span> to auto-create a branded 16:9 image from the title + keywords, or paste a URL below.
                      </p>
                    )}

                    <div className='mt-3'>
                      <Label className='text-xs text-slate-500'>Or paste a cover URL</Label>
                      <Input
                        value={selected.cover_image || ''}
                        onChange={(e) => setField('cover_image', e.target.value)}
                        placeholder='https://…/cover.jpg or paste a data URI'
                        data-testid='blog-cover-input'
                      />
                    </div>
                  </div>

                  {/* Outline builder */}
                  <div className='rounded-xl border border-dashed border-slate-300 dark:border-slate-700 p-4'>
                    <div className='flex items-center justify-between gap-2 flex-wrap'>
                      <div className='flex items-center gap-2 text-sm font-semibold text-[#0B1220] dark:text-slate-50'>
                        <ListTree className='w-4 h-4 text-[#1B3A87] dark:text-[#93C5FD]' /> Outline (helps the AI draft on-topic)
                      </div>
                      <AiButton onClick={aiGenerateOutline} loading={busy.outline} label='AI outline' testid='blog-ai-outline' />
                    </div>
                    {outline.length > 0 ? (
                      <ol className='mt-3 space-y-1 list-decimal list-inside text-sm text-slate-700 dark:text-slate-200'>
                        {outline.map((h, i) => <li key={i}>{h}</li>)}
                      </ol>
                    ) : (
                      <p className='mt-2 text-xs text-slate-500'>No outline yet. AI can generate one from your title + keywords.</p>
                    )}
                  </div>

                  <div>
                    <div className='flex items-center justify-between mb-1'>
                      <Label>Article body (Markdown)</Label>
                      <AiButton onClick={aiGenerateDraft} loading={busy.draft} label='AI write full draft' testid='blog-ai-draft' />
                    </div>
                    <Textarea
                      value={selected.content_markdown}
                      onChange={(e) => setField('content_markdown', e.target.value)}
                      placeholder='Write in Markdown. `## Heading` for sections. `**bold**`, `[link](https://…)`, `- bullet`, etc.'
                      rows={22}
                      className='font-mono text-sm'
                      data-testid='blog-body-input'
                    />
                  </div>
                </>
              )}

              {tab === 'seo' && (
                <div className='space-y-5'>
                  <div className='flex items-center justify-between'>
                    <div className='text-xs text-slate-500 dark:text-slate-400'>
                      Populate title → click <span className='font-semibold text-[#C8102E]'>AI generate</span> to fill SEO metadata + keywords.
                    </div>
                    <AiButton onClick={aiGenerateSeo} loading={busy.seo} label='AI generate SEO' testid='blog-ai-seo' />
                  </div>
                  <div>
                    <Label>SEO title (shows in Google — max ~65 chars)</Label>
                    <Input
                      value={selected.seo_title || ''}
                      onChange={(e) => setField('seo_title', e.target.value)}
                      maxLength={200}
                      data-testid='blog-seo-title-input'
                    />
                    <div className='text-[10px] text-slate-400 mt-1'>{(selected.seo_title || '').length}/65 recommended</div>
                  </div>
                  <div>
                    <Label>SEO meta description (max ~155 chars)</Label>
                    <Textarea
                      value={selected.seo_description || ''}
                      onChange={(e) => setField('seo_description', e.target.value)}
                      rows={3}
                      maxLength={320}
                      data-testid='blog-seo-desc-input'
                    />
                    <div className='text-[10px] text-slate-400 mt-1'>{(selected.seo_description || '').length}/155 recommended</div>
                  </div>
                  <div>
                    <Label>Target keywords</Label>
                    <KeywordChips
                      items={selected.keywords || []}
                      onChange={(list) => setField('keywords', list)}
                    />
                  </div>
                  <div>
                    <Label>Tags (comma separated)</Label>
                    <Input
                      value={(selected.tags || []).join(', ')}
                      onChange={(e) => setField('tags', e.target.value.split(',').map((t) => t.trim()).filter(Boolean))}
                      placeholder='dog-training, business-ops, board-and-train'
                      data-testid='blog-tags-input'
                    />
                  </div>

                  {/* Publish schedule — future-dated draft auto-publishes on
                      the backend (see blog.py _auto_publisher_loop). Default
                      slot is next Sunday 8:00 AM to match the weekly cadence. */}
                  <div className='rounded-xl border border-dashed border-[#1B3A87]/40 dark:border-[#93C5FD]/30 p-4 bg-[#1B3A87]/5 dark:bg-[#1B3A87]/10'>
                    <div className='flex items-center gap-2 text-sm font-semibold text-[#0B1220] dark:text-slate-50'>
                      <Rocket className='w-4 h-4 text-[#1B3A87] dark:text-[#93C5FD]' /> Publish schedule
                    </div>
                    <p className='text-xs text-slate-500 dark:text-slate-400 mt-1'>
                      Save the post as a draft with a future date &mdash; it will go live automatically at that time. Weekly slot defaults to <span className='font-semibold text-[#1B3A87] dark:text-[#93C5FD]'>next Sunday, 8:00 AM</span>.
                    </p>
                    <div className='mt-3 flex flex-col sm:flex-row gap-2 sm:items-end'>
                      <div className='flex-1'>
                        <Label className='text-xs'>Scheduled for</Label>
                        <Input
                          type='datetime-local'
                          value={toLocalInputValue(selected.scheduled_for)}
                          onChange={(e) => {
                            const v = e.target.value;
                            setField('scheduled_for', v ? new Date(v).toISOString() : '');
                          }}
                          data-testid='blog-schedule-input'
                        />
                      </div>
                      <Button
                        type='button'
                        variant='outline'
                        size='sm'
                        onClick={() => setField('scheduled_for', nextSunday8am().toISOString())}
                        data-testid='blog-schedule-next-sunday'
                      >
                        Next Sunday 8 AM
                      </Button>
                      {selected.scheduled_for && (
                        <Button
                          type='button'
                          variant='ghost'
                          size='sm'
                          onClick={() => setField('scheduled_for', '')}
                          data-testid='blog-schedule-clear'
                          className='text-slate-500 hover:text-red-600'
                        >
                          Clear
                        </Button>
                      )}
                    </div>
                    {selected.scheduled_for && selected.status === 'DRAFT' && (
                      <div className='mt-3 text-xs font-semibold text-[#1B3A87] dark:text-[#93C5FD] flex items-center gap-1.5'>
                        <Clock className='w-3.5 h-3.5' />
                        Auto-publishes {formatScheduled(selected.scheduled_for)}
                      </div>
                    )}
                    {selected.scheduled_for && selected.status === 'PUBLISHED' && (
                      <div className='mt-3 text-xs text-slate-500'>
                        Post is already published &mdash; scheduled time is kept for reference only.
                      </div>
                    )}
                  </div>

                  {selected.status === 'PUBLISHED' && shareLinks && (
                    <div className='rounded-xl border border-slate-200 dark:border-slate-800 p-4 bg-slate-50 dark:bg-slate-900/40'>
                      <div className='text-sm font-semibold text-[#0B1220] dark:text-slate-50 flex items-center gap-2'>
                        <Share2 className='w-4 h-4 text-[#1B3A87] dark:text-[#93C5FD]' /> Share to social
                      </div>
                      <div className='mt-3 flex flex-wrap gap-2'>
                        <a href={shareLinks.x} target='_blank' rel='noopener noreferrer' className='text-xs font-semibold px-3 py-2 rounded-lg bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 hover:border-[#1B3A87]' data-testid='blog-share-x'>Share on X</a>
                        <a href={shareLinks.facebook} target='_blank' rel='noopener noreferrer' className='text-xs font-semibold px-3 py-2 rounded-lg bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 hover:border-[#1B3A87]' data-testid='blog-share-facebook'>Share on Facebook</a>
                        <a href={shareLinks.linkedin} target='_blank' rel='noopener noreferrer' className='text-xs font-semibold px-3 py-2 rounded-lg bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 hover:border-[#1B3A87]' data-testid='blog-share-linkedin'>Share on LinkedIn</a>
                        <a href={publicUrl} target='_blank' rel='noopener noreferrer' className='text-xs font-semibold px-3 py-2 rounded-lg bg-[#1B3A87] text-white hover:bg-[#16306F] gap-1 inline-flex items-center' data-testid='blog-open-public'>
                          Open public post <ArrowUpRight className='w-3.5 h-3.5' />
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {tab === 'preview' && (
                <div className='prose prose-slate dark:prose-invert max-w-none'>
                  <div className='text-xs uppercase tracking-[0.14em] font-bold text-[#C8102E]'>Preview</div>
                  <h1 className='font-display text-3xl font-bold text-[#0B1220] dark:text-slate-50 mt-2'>{selected.title || 'Untitled'}</h1>
                  {selected.excerpt && <p className='text-slate-600 dark:text-slate-300 mt-2 italic'>{selected.excerpt}</p>}
                  <div
                    className='mt-4 text-slate-800 dark:text-slate-100'
                    dangerouslySetInnerHTML={{ __html: renderMarkdownPreview(selected.content_markdown) }}
                  />
                </div>
              )}
            </div>
          </Card>
        )}
      </div>
      )}

      {/* Confirm delete */}
      <Dialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete this post?</DialogTitle>
          </DialogHeader>
          <p className='text-sm text-slate-600 dark:text-slate-300'>This cannot be undone. The public URL will 404.</p>
          <DialogFooter>
            <Button variant='outline' onClick={() => setConfirmDelete(null)}>Cancel</Button>
            <Button className='bg-red-600 hover:bg-red-700 text-white' onClick={() => doDelete(confirmDelete)} data-testid='blog-confirm-delete'>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
